import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Pencil, Trash2, Eye, EyeOff, PlusCircle, ArrowUp, ArrowDown, Loader2, ImageUp, X } from "lucide-react";
import { api, mediaUrl, formatApiError } from "@/lib/api";

const EMPTY = { title: "", issuer: "", year: "", description: "", image_path: "", image_thumb_path: "", status: "published", display_order: 0 };

export default function AdminAwards() {
  const [awards, setAwards] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = () =>
    api.get("/admin/awards").then((r) => setAwards(r.data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));

  useEffect(() => {
    load();
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/admin/awards/${editingId}`, form);
        toast.success("Award updated");
      } else {
        await api.post("/admin/awards", form);
        toast.success("Award added");
      }
      setForm(EMPTY);
      setEditingId(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const edit = (a) => {
    setEditingId(a.id);
    setForm({
      title: a.title, issuer: a.issuer || "", year: a.year || "", description: a.description || "",
      image_path: a.image_path || "", image_thumb_path: a.image_thumb_path || "",
      status: a.status, display_order: a.display_order || 0,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const remove = async (a) => {
    if (!window.confirm(`Delete "${a.title}"?`)) return;
    await api.delete(`/admin/awards/${a.id}`);
    toast.success("Award deleted");
    load();
  };

  const toggle = async (a) => {
    await api.put(`/admin/awards/${a.id}`, {
      title: a.title, issuer: a.issuer || "", year: a.year || "", description: a.description || "",
      image_path: a.image_path || "", image_thumb_path: a.image_thumb_path || "",
      status: a.status === "published" ? "hidden" : "published", display_order: a.display_order || 0,
    });
    load();
  };

  const move = async (i, dir) => {
    const ids = awards.map((a) => a.id);
    const j = i + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    await api.put("/admin/awards/reorder", { ids });
    load();
  };

  const uploadImage = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("files", file);
      const r = await api.post("/admin/upload", fd);
      const f = r.data.files[0];
      setForm((prev) => ({ ...prev, image_path: f.storage_path, image_thumb_path: f.thumb_path || "" }));
      toast.success("Photo uploaded");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const inputCls = "w-full bg-surface-white border border-charcoal-900/15 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-copper-600";

  return (
    <div>
      <h1 className="font-serif text-3xl text-charcoal-900 mb-1">Awards &amp; Recognition</h1>
      <p className="text-sm text-charcoal-500 mb-8">Add certificates and awards anytime — published ones appear on the site instantly. Attach a photo of the certificate or moment if you have one.</p>

      <form data-testid="award-form" onSubmit={save} className="bg-surface-white border border-charcoal-900/10 rounded-2xl p-6 mb-8 flex flex-col gap-4">
        <p className="font-mono text-[11px] tracking-[0.2em] uppercase text-copper-600">{editingId ? "Edit Award" : "Add Award"}</p>
        <div className="grid sm:grid-cols-2 gap-4">
          <input data-testid="award-title-input" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Award title — e.g. Best Creative Team" aria-label="Award title" className={inputCls} />
          <input data-testid="award-issuer-input" value={form.issuer} onChange={(e) => setForm({ ...form, issuer: e.target.value })} placeholder="Issuer / organization" aria-label="Issuer" className={inputCls} />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <input data-testid="award-year-input" value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} placeholder="Year — e.g. 2024" aria-label="Year" className={inputCls} />
          <select data-testid="award-status-select" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} aria-label="Status" className={inputCls}>
            <option value="published">Published</option>
            <option value="hidden">Hidden</option>
          </select>
        </div>
        <textarea data-testid="award-description-input" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Short description (optional)" aria-label="Description" className={`${inputCls} resize-none`} />

        <div className="flex items-center gap-4">
          <button
            type="button"
            data-testid="award-image-upload"
            onClick={() => fileRef.current?.click()}
            className="inline-flex items-center gap-2 rounded-full border border-charcoal-900/20 text-charcoal-700 text-xs font-medium px-4 py-2.5 hover:border-copper-600 hover:text-copper-600 transition-colors"
          >
            {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImageUp className="w-3.5 h-3.5" />}
            {form.image_path ? "Change photo" : "Upload photo (optional)"}
          </button>
          {form.image_path && (
            <>
              <img src={mediaUrl(form.image_thumb_path || form.image_path)} alt="Award preview" className="h-14 w-auto rounded-lg object-cover border border-charcoal-900/10" />
              <button
                type="button"
                data-testid="award-image-remove"
                onClick={() => setForm({ ...form, image_path: "", image_thumb_path: "" })}
                aria-label="Remove photo"
                className="text-charcoal-400 hover:text-red-600"
              >
                <X className="w-4 h-4" />
              </button>
            </>
          )}
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadImage(e.target.files?.[0])} data-testid="award-image-file-input" />
        </div>

        <div className="flex gap-3">
          <button data-testid="award-save-button" type="submit" disabled={saving || uploading} className="inline-flex items-center gap-2 rounded-full bg-charcoal-900 text-parchment text-sm font-medium px-6 py-3 hover:bg-copper-600 disabled:opacity-60 transition-colors">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlusCircle className="w-4 h-4" />}
            {saving ? "Saving..." : editingId ? "Save Changes" : "Add Award"}
          </button>
          {editingId && (
            <button type="button" onClick={() => { setEditingId(null); setForm(EMPTY); }} className="rounded-full border border-charcoal-900/20 text-sm font-medium px-6 py-3 hover:border-copper-600 transition-colors">
              Cancel
            </button>
          )}
        </div>
      </form>

      {awards.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-charcoal-900/20 bg-surface-white/60 p-10 text-center" data-testid="awards-empty">
          <p className="font-serif text-xl">No awards yet</p>
          <p className="text-sm text-charcoal-500 mt-1">The public section stays hidden until you add your first award.</p>
        </div>
      ) : (
        <div className="flex flex-col gap-3" data-testid="admin-awards-list">
          {awards.map((a, i) => (
            <div key={a.id} data-testid={`award-row-${i}`} className="bg-surface-white border border-charcoal-900/10 rounded-xl p-4 flex items-center gap-3">
              <div className="flex flex-col gap-1">
                <button onClick={() => move(i, -1)} disabled={i === 0} aria-label="Move up" className="text-charcoal-400 hover:text-copper-600 disabled:opacity-30"><ArrowUp className="w-3.5 h-3.5" /></button>
                <button onClick={() => move(i, 1)} disabled={i === awards.length - 1} aria-label="Move down" className="text-charcoal-400 hover:text-copper-600 disabled:opacity-30"><ArrowDown className="w-3.5 h-3.5" /></button>
              </div>
              {a.image_path ? (
                <img src={mediaUrl(a.image_thumb_path || a.image_path)} alt="" className="w-14 h-14 rounded-lg object-cover border border-charcoal-900/10 shrink-0" />
              ) : null}
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm text-charcoal-900 truncate">{a.title}</p>
                <p className="text-xs text-charcoal-500 mt-0.5">{[a.issuer, a.year].filter(Boolean).join(" · ")}</p>
              </div>
              <span className={`hidden sm:inline-block font-mono text-[10px] uppercase border rounded-full px-2.5 py-1 ${a.status === "published" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-charcoal-900/5 text-charcoal-500 border-charcoal-900/15"}`}>{a.status}</span>
              <button data-testid={`award-toggle-${i}`} onClick={() => toggle(a)} aria-label="Toggle visibility" className="p-2 text-charcoal-400 hover:text-copper-600">{a.status === "published" ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}</button>
              <button data-testid={`award-edit-${i}`} onClick={() => edit(a)} aria-label="Edit" className="p-2 text-charcoal-400 hover:text-copper-600"><Pencil className="w-4 h-4" /></button>
              <button data-testid={`award-delete-${i}`} onClick={() => remove(a)} aria-label="Delete" className="p-2 text-charcoal-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
